import React, {useEffect, useMemo, useState} from 'react';
import {
  OnboardingSpotlight,
  OnboardingStepKeyEnum,
  RISK_MITIGATION_SUBSTEPS,
  type RiskSubKey,
  useOnboardingFlattenedTour,
  useOnboardingRestart,
  useOnboardingStep
} from '../../../components/Onboarding';
import {useDispatch, useSelector} from 'react-redux';
import {
  resetAllChatsStreaming,
  setShowChatbox,
  setIsNewChat,
  setLastActiveChatId
} from '../../../redux/slices/chat';
import {ChatPrefill, setChatPrefill} from '../../../components/GlobalChatBox/prefill';
import {useJwt} from '../../../helpers/common';
import {DashboardStatus} from '../../../constants/integrationTypes';
import { useOrganizationService } from '../../../services';
import { setOrganizationOnboardSettings } from '../../../redux/slices/organization';
import { useSmapFetch as useStrategyMapFetch } from '../../../hooks/Smap/useSmap';

const STORAGE_KEY = 'cadence:onboarding:risk_entity';

function loadStoredRiskEntity(): { id: string; name: string } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.id === 'string') return { id: parsed.id, name: parsed.name || '' };
    return null;
  } catch {
    return null;
  }
}

function saveStoredRiskEntity(id: string, name: string) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ id, name }));
  } catch {}
}

const baseMessagesBySubKey: Record<RiskSubKey, (entityName?: string) => string> = {
  awareness: (name?: string) => `${name ? `"${name}"` : 'This strategy map'} is At Risk. Let's investigate why.`,
  analyze: () => 'Click “Analyse Deeper” to open detailed risk analysis.',
  prefill: () => 'Here\'s a prefilled mitigation prompt. Adjust if needed.',
  send: () => 'Send to get tailored next steps.',
};

const preferredBySubKey: Record<RiskSubKey, Array<'bottom' | 'top' | 'right' | 'left'>> = {
  awareness: ['left', 'right', 'bottom', 'top'],
  analyze: ['right', 'left', 'bottom', 'top'],
  prefill: ['top', 'bottom', 'right', 'left'],
  send: ['left', 'right', 'top', 'bottom'],
};

function queryAll(selector: string): HTMLElement[] {
  return typeof document !== 'undefined' ? Array.from(document.querySelectorAll(selector)) as HTMLElement[] : [];
}

function pickTopLeft(elements: HTMLElement[]): HTMLElement | null {
  if (!elements.length) return null;
  const scored = elements
    .map(el => ({ el, rect: el.getBoundingClientRect() }))
    .sort((a, b) => {
      if (a.rect.top !== b.rect.top) return a.rect.top - b.rect.top;
      return a.rect.left - b.rect.left;
    });
  return scored[0]?.el || null;
}

function getAtRiskCard(): HTMLElement | null {
  const cards = queryAll('[data-onboarding-card="at-risk"]');
  return pickTopLeft(cards);
}

async function waitForAnalyzeButtonVisible(entityId: string | null, attempts: number = 20, delayMs: number = 80): Promise<HTMLElement | null> {
  // Assumes a 'cadence:risk-open' was dispatched prior
  for (let i = 0; i < attempts; i++) {
    const btn = document.querySelector('[data-onboarding="risk-analyze-button"]') as HTMLElement | null;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      const style = window.getComputedStyle(btn);
      const visible = rect.width > 0 && rect.height > 0 && style.visibility !== 'hidden' && parseFloat(style.opacity || '1') > 0.5;
      if (visible) return btn;
    }
    await new Promise(res => setTimeout(res, delayMs));
  }
  return null;
}

async function waitForChatInput(attempts: number = 20, delayMs: number = 100): Promise<HTMLElement | null> {
  for (let i = 0; i < attempts; i++) {
    const el = document.querySelector('[data-onboarding="chat-input-area"]') as HTMLElement | null;
    if (el) return el;
    await new Promise(res => setTimeout(res, delayMs));
  }
  return null;
}

const RiskMitigationOnboarding: React.FC = () => {
  const dispatch = useDispatch();
  const JWTdata = useJwt() as any;
  const { updateOrganization } = useOrganizationService();
  const { totalSteps, getIndexForRiskSubKey } = useOnboardingFlattenedTour();
  const restartToken = useOnboardingRestart(OnboardingStepKeyEnum.RiskMitigation);
  
  const { onboardSettings = {}, loading: orgLoading } = useSelector((s: any) => s.organization);
  // const { roadmaps = [] } = useSelector((s: any) => s.roadmap || {});
  const { data: strategyMaps = [] } = useStrategyMapFetch();
  
  const isAdmin = typeof JWTdata?.org_role === 'string' && String(JWTdata?.org_role).toLowerCase() === 'admin';  
  const hasAtRiskRoadmaps = useMemo(() => Array.isArray(strategyMaps) && strategyMaps.some((r: any) => String(r?.status || '').toLowerCase() === DashboardStatus.AT_RISK), [strategyMaps]);

  const onboardInitialized = useMemo(() => {
    const hasOnboardKeys = onboardSettings && Object.keys(onboardSettings).length > 0;
    return hasOnboardKeys;
  }, [orgLoading, onboardSettings]);

  const serverCompleted: Partial<Record<RiskSubKey, boolean>> = (onboardSettings?.risk_mitigation || {}) as any;

  const [completed, setCompleted] = useState<Record<RiskSubKey, boolean>>({ awareness: false, analyze: false, prefill: false, send: false });
  const [paused, setPaused] = useState<boolean>(false);
  const [currentSubKey, setCurrentSubKey] = useState<RiskSubKey>('awareness');
  const { show: parentShow, complete: completeRemote } = useOnboardingStep(OnboardingStepKeyEnum.RiskMitigation, { shouldShow: hasAtRiskRoadmaps, riskSubKey: currentSubKey });
  const { complete: completeRiskAll } = useOnboardingStep(OnboardingStepKeyEnum.RiskMitigation, { shouldShow: hasAtRiskRoadmaps });

  const [chosenCard, setChosenCard] = useState<HTMLElement | null>(null);
  const [storedEntity, setStoredEntity] = useState<{ id: string; name: string } | null>(() => loadStoredRiskEntity());

  useEffect(() => {
    setChosenCard(getAtRiskCard());
  }, [restartToken]);

  useEffect(() => {
    // Persist selected entity whenever we detect it from the card
    const id = chosenCard?.getAttribute('data-onboarding-entity-id') || '';
    const name = chosenCard?.getAttribute('data-onboarding-entity-name') || '';
    if (id) {
      saveStoredRiskEntity(id, name);
      setStoredEntity({ id, name });
    }
  }, [chosenCard]);

  useEffect(() => {
    // restart unpauses the flow if on this page
    setPaused(false);
  }, [restartToken]);

  const entityId = (chosenCard?.getAttribute('data-onboarding-entity-id') || storedEntity?.id || '') as string;
  const entityName = (chosenCard?.getAttribute('data-onboarding-entity-name') || storedEntity?.name || '') as string;

  const [targetRefEl, setTargetRefEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // On Start, compute next substep and ensure analyze dropdown is reopened and target is ready
    let cancelled = false;
    const doRestart = async () => {
      if (!onboardInitialized || !parentShow) return; // wait until onboarding is ready and visible
      const next = pickNextSubKey();
      if (next) setCurrentSubKey(next);
      const keyToOpen = next || currentSubKey;

      if (keyToOpen === 'analyze' && entityId) {
        if (isAdmin) {
          try {
            window.dispatchEvent(new CustomEvent('cadence:risk-open', { detail: { id: entityId } }));
          } catch {}
        }
        const btn = await waitForAnalyzeButtonVisible(entityId || null, 24, 80);
        if (!cancelled) setTargetRefEl(btn);
      } else if (keyToOpen === 'awareness') {
        if (!cancelled) setTargetRefEl(chosenCard);
      }
    };
    doRestart();
    return () => { cancelled = true; };
  }, [restartToken, entityId, currentSubKey, chosenCard, onboardInitialized, parentShow]);

  const pickNextSubKey = (): RiskSubKey | undefined => {
    for (const k of RISK_MITIGATION_SUBSTEPS) {
      const key = k as RiskSubKey;
      if (!serverCompleted?.[key] && !completed[key]) return key;
    }
    return undefined; // all done
  };

  const [chatFromRisk, setChatFromRisk] = useState<boolean>(false);

  useEffect(() => {
    const onPrefill = (e: any) => {
      if (e?.detail?.source === 'risk_mitigation') {
        setChatFromRisk(true);
        // Guard against duplicate completion/API calls
        if (currentSubKey === 'analyze' && !(completed.analyze || serverCompleted?.analyze)) {
          setCompleted(prev => ({ ...prev, analyze: true }));
          try { completeRemote(); } catch {}
          setCurrentSubKey('prefill');
        }
      }
    };
    window.addEventListener('cadence:chat-prefill', onPrefill);
    return () => window.removeEventListener('cadence:chat-prefill', onPrefill);
  }, [currentSubKey, completeRemote, completed.analyze, serverCompleted?.analyze]);

  useEffect(() => {
    if (paused) return; // user closed; don't auto-show
    const next = pickNextSubKey();
    if (next) setCurrentSubKey(next);
  }, [serverCompleted?.awareness, serverCompleted?.analyze, serverCompleted?.prefill, serverCompleted?.send, completed, paused]);

  useEffect(() => {
    let cancelled = false;
    const setEl = async () => {
      if (!onboardInitialized || !parentShow) return; // wait until onboarding is ready and visible
      if (currentSubKey === 'awareness') {
        if (!cancelled) setTargetRefEl(chosenCard);
      } else if (currentSubKey === 'analyze') {
        if (entityId && isAdmin) {
          window.dispatchEvent(new CustomEvent('cadence:risk-open', { detail: { id: entityId } }));
        }
        const btn = await waitForAnalyzeButtonVisible(entityId || null, 24, 80);
        if (!cancelled) setTargetRefEl(btn);
      } else if (currentSubKey === 'prefill') {
        const input = await waitForChatInput(20, 100);
        if (!chatFromRisk) { setTargetRefEl(input as any); return; }
        if (!cancelled) setTargetRefEl(input);
      } else {
        // SEND: spotlight only the minimized form's send button
        const form = document.querySelector('form[data-onboarding="chat-input-area"]') as HTMLFormElement | null;
        const sendBtn = form ? (form.querySelector('[data-onboarding="chat-send-button"]') as HTMLButtonElement | null) : null;
        if (!cancelled) setTargetRefEl(sendBtn as any);
        if (!sendBtn) {
          setTimeout(() => {
            if (cancelled) return;
            const f2 = document.querySelector('form[data-onboarding="chat-input-area"]') as HTMLFormElement | null;
            const btn2 = f2 ? (f2.querySelector('[data-onboarding="chat-send-button"]') as HTMLButtonElement | null) : null;
            setTargetRefEl(btn2 as any);
          }, 60);
        }
      }
    };
    setEl();
    return () => { cancelled = true; };
  }, [currentSubKey, chosenCard, entityId, chatFromRisk, onboardInitialized, parentShow]);

  const stepIndex = useMemo(() => getIndexForRiskSubKey(currentSubKey), [currentSubKey, getIndexForRiskSubKey]);

  const closeRiskTooltip = () => {
    if (!onboardInitialized) return;
    window.dispatchEvent(new CustomEvent('cadence:risk-close', { detail: { id: entityId || undefined } }));
  };

  useEffect(() => () => closeRiskTooltip(), []);
  useEffect(() => {
    if (currentSubKey !== 'analyze' && currentSubKey !== 'awareness') closeRiskTooltip();
  }, [currentSubKey]);

  const skipAll = async () => {
    try { await completeRiskAll(); } catch {}
    setPaused(true);
    closeRiskTooltip();
  };

  const openChatWithPrefill = () => {
    if (!onboardInitialized || !parentShow) return; // wait until onboarding is ready and visible
    const prompt = `Analyse why @${entityName} is at risk? Provide actionable recommendations to mitigate the risk.`;

    setChatPrefill({
      type: 'strategy_map' as any,
      id: String(entityId || ''),
      name: entityName,
      fullPrompt: prompt,
      source: 'risk_mitigation' as any
    } as ChatPrefill);

    dispatch(resetAllChatsStreaming());
    dispatch(setIsNewChat(true));
    dispatch(setLastActiveChatId(null));
    dispatch(setShowChatbox(true));
    const evt = new CustomEvent('cadence:chat-prefill', {
      detail: {
        type: 'strategy_map',
        id: String(entityId || ''),
        name: entityName,
        fullPrompt: prompt,
        source: 'risk_mitigation'
      }
    });
    window.dispatchEvent(evt);
  };

  const handleNext = async () => {
    setCompleted(prev => ({ ...prev, [currentSubKey]: true }));
    try { completeRemote(); } catch {}

    if (currentSubKey === 'awareness') {
      if (onboardInitialized && parentShow && entityId && isAdmin) {
        window.dispatchEvent(new CustomEvent('cadence:risk-open', { detail: { id: entityId } }));
      }
      const btn = await waitForAnalyzeButtonVisible(entityId || null, 24, 80);
      if (btn) setCurrentSubKey('analyze');
      return;
    }
    if (currentSubKey === 'analyze') {
      openChatWithPrefill();
      setCurrentSubKey('prefill');
      return;
    }
    if (currentSubKey === 'prefill') { setCurrentSubKey('send'); return; }
    if (currentSubKey === 'send') {
      const form = document.querySelector('form[data-onboarding="chat-input-area"]') as HTMLFormElement | null;
      const sendBtn = form ? (form.querySelector('[data-onboarding="chat-send-button"]') as HTMLButtonElement | null) : null;
      try { sendBtn?.click(); } catch {}
      setPaused(true);
      return;
    }
  };

  const nextCandidate = pickNextSubKey();
  // if (paused || !parentShow || !isCurrent(OnboardingStepKeyEnum.RiskMitigation) || !nextCandidate) {
  if (!isAdmin || paused || !parentShow || !nextCandidate || !onboardInitialized || !hasAtRiskRoadmaps) {
    closeRiskTooltip();
    return null;
  }
  if (nextCandidate !== currentSubKey) {
    setCurrentSubKey(nextCandidate);
    return null;
  }

  const targetRef = { current: (targetRefEl as any) || null } as React.RefObject<HTMLElement>;
  if (!targetRef.current) return null;

  const message = baseMessagesBySubKey[currentSubKey](entityName || undefined);

  return (
    <OnboardingSpotlight
      key={`${currentSubKey}-${stepIndex}-${restartToken}`}
      targetRef={targetRef}
      message={message}
      onDismiss={handleNext}
      onCompleteOnly={currentSubKey !== 'analyze' ? () => {
        // For non-analyze steps: complete but do not advance automatically
        setCompleted(prev => ({ ...prev, [currentSubKey]: true }));
        try { completeRemote(); } catch {}
        setPaused(true);
      } : undefined}
      onClose={async () => {
        const next = { ...(onboardSettings || {}), dismissed: true };
        dispatch(setOrganizationOnboardSettings(next));
        await updateOrganization(JWTdata?.org_id, { onboard_settings: next }, true);
      }}
      onSkip={skipAll}
      variant="spotlight"
      stepIndex={Math.max(0, stepIndex)}
      totalSteps={totalSteps}
      primaryTitle={'Next'}
      preferredPlacements={preferredBySubKey[currentSubKey]}
    />
  );
};

export default RiskMitigationOnboarding; 